import { Badge, Button, Flex, Popover, Progress, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { Info } from 'lucide-react';
import { useState } from 'react';

import { TextEllipsis } from '../../../ui/components/text';
import { CREDIT_GROUPS as GROUPS } from '../shared/credit-groups';

import { AddCreditsPopover } from './AddCreditsPopover';

import type { GetAccountResponseDtoMembershipsItemOrganizationCredits } from '../../../api/generated/model/getAccountResponseDtoMembershipsItemOrganizationCredits';

type Props = {
  organizationId: string;
  credits: GetAccountResponseDtoMembershipsItemOrganizationCredits | undefined;
  canManageBilling: boolean;
  onOpenBilling: () => void;
};

const format = (n: number) => Math.floor(n).toLocaleString();

export const CreditsUsageWidget = ({
  organizationId,
  credits,
  canManageBilling,
  onOpenBilling,
}: Props) => {
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);

  if (!credits) return null;

  const used = GROUPS.reduce((sum, group) => sum + credits.usedThisPeriod[group.key], 0);
  const total = used + credits.balance;

  return (
    <AddCreditsPopover
      open={addCreditsOpen}
      organizationId={organizationId}
      credits={credits}
      onClose={() => setAddCreditsOpen(false)}
      onOpenBilling={() => {
        setAddCreditsOpen(false);
        onOpenBilling();
      }}
    >
      <Flex vertical gap={8} data-testid="credits-widget" style={{ padding: '8px 18px 14px' }}>
        <Flex align="center" gap={8}>
          <TextEllipsis style={{ fontSize: 13, fontWeight: 500, lineHeight: '22px' }}>
            Usage this cycle
          </TextEllipsis>
          {canManageBilling && (
            <Button
              type="primary"
              size="small"
              onClick={() => setAddCreditsOpen((open) => !open)}
              data-testid="credits-add"
              style={{ height: 22, padding: '3px 9px', fontSize: 11 }}
            >
              + Add credits
            </Button>
          )}
          <Flex align="center" gap={4} flex="none" style={{ marginLeft: 'auto' }}>
            <Typography.Text
              type="secondary"
              className="font-mono"
              style={{ fontSize: 12, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
              data-testid="credits-used"
            >
              {format(used)} / {format(total)}
            </Typography.Text>
            {credits.debt > 0 && (
              <Tooltip
                title={`Your company owes ${format(credits.debt)} credits. They are repaid first from the next renewal or top-up.`}
              >
                <Info size={12} color="var(--ant-color-warning)" data-testid="credits-debt" />
              </Tooltip>
            )}
          </Flex>
        </Flex>

        <Popover
          placement="left"
          styles={{ root: { width: 280 } }}
          align={{ offset: [-32, 0] }}
          content={
            <Flex vertical gap={12}>
              <Flex align="center" justify="space-between" gap={8}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  Credit breakdown
                </Typography.Text>
                {credits.renewsAt && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    resets {dayjs(credits.renewsAt).format('MMM D')}
                  </Typography.Text>
                )}
              </Flex>
              {GROUPS.map((group) => (
                <Flex key={group.key} vertical data-testid={`credits-breakdown-${group.key}`}>
                  <Flex align="center" justify="space-between" gap={8}>
                    <Badge
                      color={group.color}
                      text={
                        <Typography.Text style={{ fontSize: 13 }}>{group.label}</Typography.Text>
                      }
                    />
                    <Typography.Text
                      type="secondary"
                      className="font-mono"
                      style={{ fontSize: 12 }}
                    >
                      {format(credits.usedThisPeriod[group.key])} used
                    </Typography.Text>
                  </Flex>
                  <Progress
                    percent={total > 0 ? (credits.usedThisPeriod[group.key] / total) * 100 : 0}
                    showInfo={false}
                    strokeColor={group.color}
                    size={{ height: 4 }}
                  />
                </Flex>
              ))}
            </Flex>
          }
        >
          <Flex
            style={{
              height: 8,
              borderRadius: 9999,
              overflow: 'hidden',
              background: 'var(--ant-color-fill-tertiary)',
            }}
          >
            {GROUPS.map(
              (group) =>
                credits.usedThisPeriod[group.key] > 0 && (
                  <div
                    key={group.key}
                    data-testid={`credits-used-${group.key}`}
                    style={{
                      width: `${(credits.usedThisPeriod[group.key] / total) * 100}%`,
                      height: '100%',
                      background: group.color,
                      transition: 'width 300ms',
                    }}
                  />
                ),
            )}
          </Flex>
        </Popover>

        <Typography.Text
          data-testid="credits-remaining"
          style={{
            fontSize: 12,
            color: credits.exhausted
              ? 'var(--ant-color-error)'
              : credits.low
                ? 'var(--ant-color-warning)'
                : 'var(--ant-color-text-tertiary)',
          }}
        >
          {format(credits.balance)} credits remaining across AI, calling, texting & data reveal
        </Typography.Text>
      </Flex>
    </AddCreditsPopover>
  );
};
